import { getWeeksBetween, getWeeklyGroups } from './dateHelpers';

/**
 * Generate a weekly rotation schedule for residents
 * @param {Array} filteredResidents - Array of resident objects
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {number} weekDuration - Number of weeks per rotation block
 * @param {Array} selectedRotations - Array of rotation names to assign
 * @param {Object} rotationFrequency - Object mapping rotation names to frequency counts
 * @param {Array} recurrentRotations - Array of rotations that can be repeated
 * @returns {Object} Schedule object with resident IDs as keys
 */
export const generateWeeklySchedule = async (
  filteredResidents,
  startDate,
  endDate,
  weekDuration,
  selectedRotations,
  rotationFrequency,
  recurrentRotations
) => {
  // Get all weeks between start and end date
  const weeks = getWeeksBetween(startDate, endDate);
  const totalWeeks = weeks.length;
  const totalRotations = Math.ceil(totalWeeks / weekDuration);

  if (totalWeeks === 0) {
    throw new Error("No valid weeks found between start and end dates");
  }

  // Initialize schedule
  const newSchedule = {};
  filteredResidents.forEach((resident) => {
    newSchedule[resident.id] = {
      name: `${resident.firstName} ${resident.lastName}`,
      year: parseInt(resident.year),
      rotations: {},
    };
    weeks.forEach((week) => {
      newSchedule[resident.id].rotations[week.start] = null;
    });
  });

  // Create rotation assignments with even distribution
  const rotationAssignments = [];
  
  // Calculate total rotation count including frequency
  const totalRotationCount = selectedRotations.reduce((total, rotation) => {
    const frequency = rotationFrequency[rotation] || 1;
    return total + frequency;
  }, 0);
  
  // Create expanded rotation list based on frequency
  const expandedRotations = [];
  selectedRotations.forEach(rotation => {
    const frequency = rotationFrequency[rotation] || 1;
    for (let i = 0; i < frequency; i++) {
      expandedRotations.push(rotation);
    }
  });
  
  if (totalRotationCount >= totalRotations) {
    // We have enough rotations - distribute evenly
    rotationAssignments.push(...expandedRotations.slice(0, totalRotations));
  } else {
    // We need to repeat some rotations - distribute them evenly
    const rotationsToRepeat = recurrentRotations.length > 0 
      ? recurrentRotations 
      : selectedRotations;
    
    // Create expanded list of rotations to repeat
    const expandedRotationsToRepeat = [];
    rotationsToRepeat.forEach(rotation => {
      const frequency = rotationFrequency[rotation] || 1;
      for (let i = 0; i < frequency; i++) {
        expandedRotationsToRepeat.push(rotation);
      }
    });
    
    // Calculate how many times each rotation should appear
    const rotationCounts = {};
    expandedRotationsToRepeat.forEach(rotation => {
      rotationCounts[rotation] = (rotationCounts[rotation] || 0) + 1;
    });
    
    // Calculate how many additional times each rotation needs to be repeated
    const additionalCounts = {};
    Object.keys(rotationCounts).forEach(rotation => {
      const currentCount = rotationCounts[rotation];
      const totalNeeded = Math.ceil(totalRotations / expandedRotationsToRepeat.length) * currentCount;
      additionalCounts[rotation] = Math.max(0, totalNeeded - currentCount);
    });
    
    // Create a distribution plan
    const distributionPlan = [];
    Object.keys(additionalCounts).forEach(rotation => {
      const additional = additionalCounts[rotation];
      for (let i = 0; i < additional; i++) {
        distributionPlan.push(rotation);
      }
    });
    
    // Shuffle the distribution plan for randomness
    const shuffledPlan = distributionPlan.sort(() => Math.random() - 0.5);
    
    // Combine original rotations with additional ones
    rotationAssignments.push(...expandedRotationsToRepeat, ...shuffledPlan);
    
    // Trim to exact number of rotations needed
    if (rotationAssignments.length > totalRotations) {
      rotationAssignments.splice(totalRotations);
    } else if (rotationAssignments.length < totalRotations) {
      // Fill remaining slots with rotations that need more coverage
      const remainingSlots = totalRotations - rotationAssignments.length;
      const rotationsNeedingMore = Object.keys(additionalCounts).filter(rotation => 
        additionalCounts[rotation] > 0
      );
      
      for (let i = 0; i < remainingSlots; i++) {
        const rotationToAdd = rotationsNeedingMore[i % rotationsNeedingMore.length];
        rotationAssignments.push(rotationToAdd);
      }
    }
  }

  // Assign rotations to residents with constraints
  const residentIds = Object.keys(newSchedule);
  const MAX_PER_WEEK_PER_ROTATION = 2; // mirror monthly cap (2 per month) at weekly granularity
  
  // Track rotation assignments per week across all residents
  const rotationAssignmentsPerWeek = {};
  weeks.forEach((week) => {
    rotationAssignmentsPerWeek[week.start] = {};
  });
  
  // Track each resident's rotation usage (per block), honoring frequency
  const residentRotationUsage = {};
  residentIds.forEach((residentId) => {
    residentRotationUsage[residentId] = {};
    selectedRotations.forEach((rotation) => {
      residentRotationUsage[residentId][rotation] = 0;
    });
  });
  
  // Precompute week groups by block
  const weekGroups = [];
  for (let i = 0; i < weeks.length; i += weekDuration) {
    weekGroups.push(weeks.slice(i, i + weekDuration));
  }

  // Assign by blocks to ensure same rotation across the configured weekDuration
  residentIds.forEach((residentId, residentIndex) => {
    let previousRotation = null; // per resident, prior block

    weekGroups.forEach((groupWeeks, groupIndex) => {
      // Build candidate rotations respecting previous rotation and resident frequency per block
      let candidates = rotationAssignments.filter((r) => {
        if (previousRotation === r) return false; // avoid back-to-back across blocks
        const currentUsage = residentRotationUsage[residentId][r] || 0;
        const requiredFrequency = rotationFrequency[r] || 1;
        if (currentUsage >= requiredFrequency) return false; // respect frequency per block
        // Enforce weekly cap (2 per week) for all weeks in this block
        const fitsAllWeeks = groupWeeks.every((week) => {
          const usage = rotationAssignmentsPerWeek[week.start][r] || 0;
          return usage < MAX_PER_WEEK_PER_ROTATION;
        });
        return fitsAllWeeks;
      });

      // If none fit the cap, relax the weekly cap but keep other constraints
      if (candidates.length === 0) {
        candidates = rotationAssignments.filter((r) => {
          if (previousRotation === r) return false;
          const currentUsage = residentRotationUsage[residentId][r] || 0;
          const requiredFrequency = rotationFrequency[r] || 1;
          if (currentUsage >= requiredFrequency) return false;
          return true;
        });
      }

      // Final fallback to any rotation if still empty
      if (candidates.length === 0) {
        candidates = rotationAssignments;
      }

      // Choose rotation minimizing total usage across weeks in this block
      const usageScore = (rot) => groupWeeks.reduce((sum, w) => sum + (rotationAssignmentsPerWeek[w.start][rot] || 0), 0);
      let minScore = Infinity;
      candidates.forEach((r) => {
        const score = usageScore(r);
        if (score < minScore) minScore = score;
      });
      const leastUsed = candidates.filter((r) => usageScore(r) === minScore);
      const assignedRotation = leastUsed[Math.floor(Math.random() * leastUsed.length)];

      // Assign the same rotation for all weeks in the block
      groupWeeks.forEach((week) => {
        newSchedule[residentId].rotations[week.start] = assignedRotation;
        const weekUsage = rotationAssignmentsPerWeek[week.start];
        weekUsage[assignedRotation] = (weekUsage[assignedRotation] || 0) + 1;
      });

      // Update per-block usage and previous
      residentRotationUsage[residentId][assignedRotation] = (residentRotationUsage[residentId][assignedRotation] || 0) + 1;
      previousRotation = assignedRotation;
    });
  });

  return newSchedule;
};

